<script lang="ts" setup>
import { computed, onBeforeMount, ref, toRefs } from 'vue';
import Menu from 'primevue/menu';
import { useMessageCenterStore } from '@/store';
import { EMIT } from '@/enum';

const emit = defineEmits([EMIT.DELETE_MESSAGE, EMIT.DELETE_MESSAGE_FILE]);
const messageCenterStore = useMessageCenterStore();

interface MessageActions {
  messageId?: number;
  documentId?: string;
  action: string;
  createdAt: Date | string;
}

const props = defineProps<MessageActions>();

const { createdAt, messageId, documentId, action } = toRefs(props);
const menu = ref();
const reasonId = ref();
const confirmArchiveOpen = ref(false);
const loading = ref(false);

onBeforeMount(() => init());

const init = () => {
  loading.value = false;
  confirmArchiveOpen.value = false;
  reasonId.value = false;
};

const deleteFile = async () => {
  if (!documentId.value || !messageId.value) return;
  await messageCenterStore.deleteMessageFile(messageId.value, documentId.value);

  emit(EMIT.DELETE_MESSAGE);
};

const deleteMessage = async () => {
  if (!messageId.value) return;
  await messageCenterStore.deleteMessage(messageId.value);

  if (messageCenterStore?.error) {
    messageCenterStore.doToast(messageCenterStore?.error, 'Error', true);
    return;
  }

  emit(EMIT.DELETE_MESSAGE_FILE);
};

const isRecent = computed(() => {
  const created = new Date(createdAt.value).getTime();
  const now = Date.now();
  const diffMinutes = (now - created) / (1000 * 60);
  return diffMinutes < 60;
});

const menuItems = (() => {
  const eventToEmit = action.value;

  return [
    {
      label: 'Delete Message',
      icon: {
        name: 'trash',
        styles: 'inline-block text-lg text-blue-500',
      },
      styles: '',
      visible: eventToEmit === EMIT.DELETE_MESSAGE,
      command: deleteMessage,
    },
    {
      label: 'Delete File',
      icon: {
        name: 'trash',
        styles: 'inline-block text-lg text-blue-500',
      },
      styles: '',
      visible: eventToEmit === EMIT.DELETE_MESSAGE_FILE,
      command: deleteFile,
    },
  ];
})();

const toggle = (event: Event): void => menu.value.toggle(event);
</script>

<template>
  <div v-if="isRecent" class="ml-3 cursor-pointer" @click="toggle">
    <Icon name="more_horiz" class="text-lg p-1" />
    <Menu id="overlay_menu" ref="menu" :model="menuItems" :popup="true">
      <template #item="{ item }">
        <div class="p-2">
          <a v-if="item.command">
            <span class="relative inline-block text-sm text-800" v-text="item.label" />
          </a>
        </div>
      </template>
    </Menu>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-menu) {
  width: auto !important;
}
</style>
